import type { Document } from '@contentful/rich-text-types';
import CTA from 'components/UI/Cta';
import RichText from 'components/UI/Richtext';
import { ReactElement, useState } from 'react';

export interface StateCityCtaProps {
  data: IStateCityCta[];
  heading?: Document;
}

export interface IStateCityCta {
  state: string;
  city: string;
  url: string;
  ctaText: string;
}

const renderSelect = (
  label: string,
  options: string[],
  onChange: (value: string) => void
): ReactElement => {
  return (
    <div className="w-full">
      <label htmlFor={`${label}-select`} className="block">
        <span className="block mb-2 font-heading text-primary-actual">{label}</span>
        <div className="relative">
          <select
            name={label}
            id={`${label}-select`}
            className="block w-full border border-gray-300 rounded-md bg-white px-4 py-3 pr-12 text-primary-actual appearance-none font-thin"
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{`Select ${label.toLowerCase()}`}</option>
            {options.map((value) => {
              return (
                <option key={value} value={value}>
                  {value}
                </option>
              );
            })}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-secondary-actual">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1.25L6 6.25L11 1.25"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </label>
    </div>
  );
};

export default function StateCityCta({ data, heading }: StateCityCtaProps): ReactElement {
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const states = Array.from(new Set(data.map((d) => d.state)));
  const cities = data.filter((d) => d.state === selectedState && d.city !== '').map((d) => d.city);
  const selectedUrl = data.find((d) => d.state === selectedState && d.city === selectedCity)?.url;
  const selectedCTAText = data.find((d) => d.state === selectedState && d.city === selectedCity)
    ?.ctaText;
  const openInNewTab = selectedUrl?.startsWith('http');

  const onStateChange = (value: string): void => {
    setSelectedState(value);
    setSelectedCity('');
  };

  return (
    <div className="bg-muted py-10 md:py-20 px-site-component">
      <div className="max-w-screen-max mx-auto grid gap-8 lg:grid-cols-2 lg:items-start">
        <div className="flex items-stretch bordered-heading-richtext">
          {heading ? (
            <RichText
              document={heading}
              classNames="font-heading text-new-34 lg:text-4xl lg:leading-new-43 text-primary-actual"
            />
          ) : (
            <h2 className="font-heading text-new-34 lg:text-4xl lg:leading-new-43 text-primary-actual">
              Clarkson Eyecare Local Eye Doctor Directory
            </h2>
          )}
        </div>

        <div className="w-full lg:max-w-600 lg:justify-self-end">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end text-lg">
            <div className="flex-1">{renderSelect('Search by state', states, onStateChange)}</div>
            {cities.length > 0 && (
              <div className="flex-1">
                {renderSelect('Search by city', cities, (value: string) => setSelectedCity(value))}
              </div>
            )}
            {selectedUrl && (
              <div className="lg:flex-none">
                <CTA
                  text={selectedCTAText || ''}
                  slug={selectedUrl}
                  colorScheme="Primary"
                  openInNewTab={openInNewTab}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
